import React, { useEffect } from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { getProductImageURL, isKeyboardTriggeredEvent } from '@noths/polaris-client-utils';
import { FavouriteButton } from '@noths/polaris-design-system-candidate-components';
import Image from 'next/image';

import { CarouselDots } from 'src/components/atoms/CarouselDots/CarouselDots';
import {
  NextButton,
  PreviousButton,
} from 'src/components/atoms/ImageNavigationButtons/ImageNavigationButtons';
import { ImageNumberLabel } from 'src/components/atoms/ImageNumberLabel/ImageNumberLabel';
import { PreventSwipeNavigation } from 'src/components/molecules/PreventSwipeNavigation/PreventSwipeNavigation';
import { ProductVideoSlide } from 'src/components/molecules/ProductVideoSlide/ProductVideoSlide';
import type {
  MediaItem,
  MediaLocation,
  NextOrPreviousPayload,
  OpenModalPayload,
  ProductMediaState,
  SetCurrentMediaPayload,
} from 'src/components/organisms/ProductMediaCarousel/types';
import {
  PRODUCT_MEDIA_TABPANEL_CLASS,
  PRODUCT_MEDIA_TABPANEL_CONTAINER_ID,
  PRODUCT_MEDIA_TABPANEL_ID_PREFIX,
} from './constants/elementIds';
import { mediaKey } from './media/buildProductMedia';
import { ProductMediaGridLayout } from './ProductMediaGridLayout';
import { useProductMediaCarousel } from './useProductMediaCarousel';
import * as styles from './ProductMediaCarousel.styles';

export interface ProductMediaCarouselProps {
  currentMedia: ProductMediaState['currentMedia'];
  favouriteProduct?: boolean;
  idPrefix?: string;
  location: MediaLocation;
  media: MediaItem[];
  mobileEdgeToEdge?: boolean;
  navigatedBy: ProductMediaState['navigatedBy'];
  onFavouriteClick?: (productCode: number) => void;
  onNextMedia: (payload: NextOrPreviousPayload) => void;
  onOpenModal?: (payload: OpenModalPayload) => void;
  onPrevMedia: (payload: NextOrPreviousPayload) => void;
  onVisible?: (payload: { totalMedia: number }) => void;
  productCode?: number;
  sectionLabel: string;
  setCurrentMedia: (payload: SetCurrentMediaPayload) => void;
  showArrows?: boolean;
  showDots?: boolean;
  showFavouriteButton?: boolean;
  showNumberOfSlides?: boolean;
  showsAsGrid?: boolean;
  /** Pause the scroll/state sync while another surface (e.g. the full screen modal) owns navigation */
  suspendScrollSync?: boolean;
}

export const ProductMediaCarousel = ({
  currentMedia,
  favouriteProduct,
  idPrefix,
  location,
  media,
  mobileEdgeToEdge,
  navigatedBy,
  onFavouriteClick,
  onNextMedia,
  onOpenModal,
  onPrevMedia,
  onVisible,
  productCode,
  sectionLabel,
  setCurrentMedia,
  showArrows,
  showDots,
  showFavouriteButton,
  showNumberOfSlides,
  showsAsGrid,
  suspendScrollSync,
}: ProductMediaCarouselProps) => {
  const totalMedia = media.length;
  const impressionRef = useImpressionRef<HTMLElement>(() => {
    onVisible?.({ totalMedia });
  });
  const containerId = `${idPrefix ?? ''}${PRODUCT_MEDIA_TABPANEL_CONTAINER_ID}`;
  const slideId = (i: number) => `${idPrefix ?? ''}${PRODUCT_MEDIA_TABPANEL_ID_PREFIX}${i}`;
  const [mediaFocus, setMediaFocus] = React.useState(false);
  const [hasInteractedWithCarousel, setHasInteractedWithCarousel] = React.useState(false);

  useEffect(() => {
    if (currentMedia > 0) {
      // User has navigated past the first media item
      setHasInteractedWithCarousel(true);
    }
  }, [currentMedia]);

  const [carouselContainerRef, carouselItemRefs] = useProductMediaCarousel(
    (mediaIndex) => setCurrentMedia({ mediaIndex, navigationType: 'swipe', location }),
    currentMedia,
    navigatedBy,
    totalMedia,
    suspendScrollSync,
  );

  const favouriteButtonElement = showFavouriteButton &&
    onFavouriteClick &&
    productCode !== undefined && (
      <FavouriteButton
        active={!!favouriteProduct}
        css={styles.favouriteButton}
        onClick={() => onFavouriteClick(productCode)}
        size="lg"
      />
    );

  if (showsAsGrid) {
    return (
      <section
        aria-label={sectionLabel}
        css={styles.carouselContainer}
        ref={onVisible ? impressionRef : null}
      >
        <ProductMediaGridLayout
          location={location}
          media={media}
          onSelect={(mediaIndex: number) => {
            setCurrentMedia({ mediaIndex, navigationType: 'click', location });
            onOpenModal?.({ location });
          }}
        />
        {favouriteButtonElement}
      </section>
    );
  }

  return (
    <PreventSwipeNavigation aria-live="polite" css={mobileEdgeToEdge && styles.mobileEdgeToEdge}>
      <section
        aria-label={sectionLabel}
        css={[styles.carouselContainer]}
        ref={onVisible ? impressionRef : null}
      >
        <div
          css={styles.mainMediaWrapperWithThumbnails}
          data-media-focus={mediaFocus}
          id={containerId}
          ref={carouselContainerRef}
        >
          {media.map((item, i) => {
            if (item.type === 'video') {
              return (
                <div
                  className={PRODUCT_MEDIA_TABPANEL_CLASS}
                  css={styles.carouselSlide}
                  data-item-index={i}
                  data-media-focus={mediaFocus}
                  id={slideId(i)}
                  key={mediaKey(item)}
                  ref={(node) => {
                    carouselItemRefs.current[i] = node as HTMLElement;
                  }}
                >
                  <ProductVideoSlide
                    contentPosition={i + 1}
                    isActive={currentMedia === i}
                    location={location}
                    onExpand={onOpenModal ? () => onOpenModal({ location }) : undefined}
                    playbackId={item.playbackId}
                    posterUrl={item.posterUrl}
                    title={item.title}
                    totalOptions={totalMedia}
                  />
                </div>
              );
            }

            const { href: src, title } = item;
            const label = `${title}, ${i + 1} of ${totalMedia}`;

            if (!onOpenModal) {
              return (
                <div
                  className={PRODUCT_MEDIA_TABPANEL_CLASS}
                  css={styles.carouselSlide}
                  data-item-index={i}
                  id={slideId(i)}
                  key={mediaKey(item)}
                  ref={(node) => {
                    carouselItemRefs.current[i] = node as HTMLElement;
                  }}
                >
                  <Image
                    alt={label}
                    css={styles.mainMedia}
                    fill={true}
                    priority={i === 0}
                    src={getProductImageURL(src, 'normal')}
                    unoptimized
                  />
                </div>
              );
            }

            return (
              <button
                aria-label={label}
                className={PRODUCT_MEDIA_TABPANEL_CLASS}
                css={styles.carouselSlide}
                data-item-index={i}
                data-media-focus={mediaFocus}
                id={slideId(i)}
                key={mediaKey(item)}
                onBlur={() => setMediaFocus(false)}
                onClick={() => onOpenModal({ location })}
                onFocus={(e) => setMediaFocus(isKeyboardTriggeredEvent(e))}
                ref={(node) => {
                  carouselItemRefs.current[i] = node as HTMLElement;
                }}
                tabIndex={currentMedia === i ? undefined : -1}
                type="button"
              >
                <Image
                  alt={label}
                  css={styles.mainMedia}
                  fill={true}
                  priority={i === 0}
                  src={getProductImageURL(
                    src,
                    i === 0 || hasInteractedWithCarousel ? 'original' : 'normal',
                  )}
                  unoptimized
                />
              </button>
            );
          })}
        </div>
        {showArrows && totalMedia > 1 && (
          <>
            <PreviousButton
              controls={containerId}
              onClick={() => onPrevMedia({ location, totalMedia })}
            />
            <NextButton
              controls={containerId}
              onClick={() => onNextMedia({ location, totalMedia })}
            />
          </>
        )}
        {showNumberOfSlides && totalMedia > 1 && (
          <div css={styles.imageNumberLabelContainer}>
            <ImageNumberLabel currentImage={currentMedia + 1} totalImages={totalMedia} />
          </div>
        )}
        {favouriteButtonElement}
      </section>
      {showDots && totalMedia > 1 && (
        <CarouselDots currentMedia={currentMedia} totalMedia={totalMedia} />
      )}
    </PreventSwipeNavigation>
  );
};
